import { Injectable } from '@angular/core';
import { BackendReminder } from '../reminder-fields.type';
import { BehaviorSubject, Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import { Reminder } from '../reminder.model';
import { ReminderCrudService } from '../reminder-crud/reminder-crud.service';
import { ServiceAnswer } from '../../shared/service-answer';
import { AppMessageService } from '../../shared/app-message/app-message.service';
import { AuthService } from '~app/auth/auth.service';

@Injectable()
export class ReminderService {
  private activeRemindersSubject = new BehaviorSubject<Reminder[]>([]);
  public activeReminders$ = this.activeRemindersSubject.asObservable();
  public initialized$ = new Subject<void>();

  constructor(
    public remindersCrud: ReminderCrudService,
    public appMessageService: AppMessageService,
    private authService: AuthService) {
    this.authService.login$.pipe(take(1)).subscribe(() => {
      this.init();
    });
  }

  public init() {
    this.loadRemindersFromBackend();
  }

  public async createReminder(backendReminder: BackendReminder): Promise<ServiceAnswer<BackendReminder>> {
    const serviceAnswer = await this.remindersCrud.create(backendReminder);
    if (serviceAnswer.isOk) {
      this.loadRemindersFromBackend();
    }
    return serviceAnswer;
  }

  public async editReminder(backendReminder: BackendReminder): Promise<ServiceAnswer<BackendReminder>> {
    const serviceAnswer = await this.remindersCrud.edit(backendReminder);
    if (serviceAnswer.isOk) {
      this.loadRemindersFromBackend();
    }
    return serviceAnswer;
  }

  private loadRemindersFromBackend() {
    // Get alarms from backend, parse service answer and convert types
    this.remindersCrud.getReminders()
      .then(res => {
        const receivedReminders: BackendReminder[] = res;
        const activeReminders = receivedReminders
        .filter(recAlarm => recAlarm.isActive)
        .map(backendReminder => new Reminder(backendReminder, this.handleReminderCancelled));
        for (const reminder of activeReminders) {
          reminder.getAlarmTriggeringPromise()
          .then(triggeredReminder => { // Reminder time is up
            this.appMessageService.broadcastReminder(triggeredReminder);
          })
          .catch(console.error);
        }
        this.activeRemindersSubject.next(activeReminders);
      })
      .catch(console.warn);
      /**
       * Emit initialized$ as the request in made, but before responded
       * This is used for synchronizing tests
       */
      this.initialized$.next();
      this.initialized$.complete();
  }

  /**
   * Update backend with cancelled alarm and reload data
   * @param reminder
   */
  private handleReminderCancelled = (reminder: Reminder) => {
    // Make sure the notification and toast are hidden
    this.appMessageService.hideReminder(reminder);
    // Update the data
    const cancelledReminder = reminder.backendReminder;
    cancelledReminder.isActive = false;
    this.remindersCrud.edit(reminder.backendReminder)
      .then(_ => this.loadRemindersFromBackend());
  }
}
